import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'
import { Video, Search } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { searchSavedItems, generateIdeas, type SavedItem } from '@/services/shorts-service'
import { api } from '@/utils/api'
import { useToast } from '@/hooks/use-toast'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { Skeleton } from '@/components/ui/skeleton'

export function ShortsNarratorPage() {
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<SavedItem[]>([])
  type SelectedRef = { type: 'profile'|'trend'; id: string; label: string }
  const [selected, setSelected] = useState<SelectedRef[]>([])
  const [contextText, setContextText] = useState('')
  const { toast } = useToast()
  type Ideas = { controversial?: string[]; mentor?: string[]; newsflash?: string[]; debunk?: string[] }
  const [generated, setGenerated] = useState<Ideas | null>(null)
  const [reelType, setReelType] = useState<'quick'|'typical'|'detailed'>('typical')
  const [selectedIdea, setSelectedIdea] = useState<{ category: keyof Ideas; index: number; text: string } | null>(null)
  const [generating, setGenerating] = useState(false)
  const [extraContext, setExtraContext] = useState<Array<{ query: string; summary: string; sources: { title: string; url: string }[] }>>([])
  const [buildingContext, setBuildingContext] = useState(false)
  const [buildingScript, setBuildingScript] = useState(false)
  const [script, setScript] = useState<any>(null)

  const searchRef = useRef<number>(0)
  useEffect(() => {
    let cancelled = false
    const reqId = ++searchRef.current
    const handler = setTimeout(async () => {
      const q = query.trim()
      // Require a non-empty query (min 2 chars) to search; do not list all saved by default
      if (q.length < 2) {
        setItems([])
        setLoading(false)
        return
      }
      setLoading(true)
      try {
        const resp = await searchSavedItems({ q, limit: 10 })
        if (cancelled || reqId !== searchRef.current) return
        setItems(resp.items || [])
      } finally {
        if (!cancelled && reqId === searchRef.current) setLoading(false)
      }
    }, 250)
    return () => { cancelled = true; clearTimeout(handler) }
  }, [query])

  const toggleSelect = (it: SavedItem) => {
    const ref: SelectedRef = {
      type: it.type,
      id: it.id,
      label: it.type === 'profile' ? (it as any).title : (it as any).websearch_query,
    }
    setSelected((prev) => {
      const exists = prev.find((p) => p.type === ref.type && p.id === ref.id)
      const next = exists ? prev.filter((p) => !(p.type === ref.type && p.id === ref.id)) : [...prev, ref]
      // If we just added a selection from the dropdown, clear the query and list
      if (!exists) {
        setQuery('')
        setItems([])
      }
      return next
    })
  }

  const create = async () => {
    try {
      setGenerating(true)
      const context_items = selected.map((s) => ({ type: s.type, id: s.id })) as Array<{ type: 'profile'|'trend'; id: string }>
      const ideas = await generateIdeas({ context_text: contextText, context_items })
      setGenerated(ideas)
      toast({ title: 'Personalized ideas generated' })
    } catch (e) {
      toast({ title: 'Failed', description: 'Could not generate ideas', variant: 'destructive' })
    } finally {
      setGenerating(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-red-400/10 to-transparent dark:from-red-400/5 rounded-full -translate-y-8 translate-x-8" />
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <CardTitle className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <Video className="h-6 w-6 text-red-600 dark:text-red-400" />
                Shorts Narrator
              </CardTitle>
              <p className="text-muted-foreground">Highly personalized transcripts that are grounded in human attention psychology</p>
            </div>
            <Badge variant="secondary" className="text-xs">
              AI-Generated
            </Badge>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle>Select Context</CardTitle>
          <CardDescription>Pick saved profiles and trends; add any extra text context.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search saved items..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="max-w-sm"
            />
          </div>

          <div className="rounded-md border border-border/50 max-h-64 overflow-y-auto bg-accent/30">
            <div className="divide-y divide-border/50">
              {items.length === 0 && !loading && (
                <div className="p-3 text-sm text-muted-foreground">Type at least 2 characters to search your saved items.</div>
              )}
              {items.map((it) => (
                <div
                  key={`${it.type}-${it.id}`}
                  className={`flex items-center justify-between px-3 py-1.5 cursor-pointer transition-colors ${selected.find((s) => s.type===it.type && s.id===it.id) ? 'bg-primary/5' : 'hover:bg-muted/40'}`}
                  onClick={() => toggleSelect(it)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSelect(it) } }}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">
                      {it.type === 'profile' ? (it as any).title : (it as any).websearch_query}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {it.type === 'profile' ? (it as any).snippet : (it as any).summary}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground ml-3">
                    {selected.find((s) => s.type===it.type && s.id===it.id) ? 'Selected' : 'Select'}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Selected cards */}
          {selected.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-medium">Selected</div>
              <div className="grid gap-2 sm:grid-cols-2">
                {selected.map((s) => (
                  <Card key={`${s.type}-${s.id}`} className="border border-border/50">
                    <CardHeader className="py-2">
                      <div className="flex items-center justify-between gap-2">
                        <CardTitle className="text-sm font-semibold truncate">{s.label}</CardTitle>
                        <Badge variant="outline" className="text-xs capitalize">{s.type}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="py-1">
                      <div className="flex justify-end">
                        <Button size="sm" variant="ghost" onClick={() => setSelected((prev) => prev.filter((p) => !(p.type===s.type && p.id===s.id)))}>Remove</Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          <Separator />

          <div className="space-y-2">
            <div className="text-sm font-medium">Additional context</div>
            <textarea
              value={contextText}
              onChange={(e) => setContextText(e.target.value)}
              className="w-full h-20 rounded border border-border/50 bg-background p-2 text-sm"
              placeholder="Add any rough ideas you have, or just leave it blank."
            />
          </div>

          <div className="flex justify-end">
            <Button onClick={create} disabled={generating || loading || (selected.length===0 && !contextText.trim())}>{generating ? 'Generating...' : 'Get Personalized Content Ideas'}</Button>
          </div>
        </CardContent>
      </Card>

      {generated && (
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-50/50 dark:bg-blue-950/20 flex-shrink-0">
                <Search className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <CardTitle className="text-lg font-semibold">Pick an idea</CardTitle>
                <CardDescription>Two concise ideas per category. Select one to proceed.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              {(
                ['controversial','mentor','newsflash','debunk'] as Array<keyof Ideas>
              ).map((k) => (
                <div key={k} className="rounded border border-border/50 p-3 bg-accent/30">
                  <div className="font-semibold mb-2 capitalize text-primary">{k}</div>
                  <div className="space-y-2">
                    {(generated?.[k] ?? []).map((line: string, idx: number) => {
                      const isSelected = selectedIdea?.category === k && selectedIdea?.index === idx
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => setSelectedIdea({ category: k, index: idx, text: line })}
                          className={`w-full text-left rounded border px-3 py-2 transition-colors ${isSelected ? 'border-primary bg-primary/5' : 'border-border/50 hover:bg-muted/40'}`}
                        >
                          {line}
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-xs text-muted-foreground flex-1">
                {selectedIdea ? `Selected: ${selectedIdea.text.slice(0, 80)}${selectedIdea.text.length > 80 ? '…' : ''}` : 'Select one idea to continue'}
              </div>
              <div className="flex items-center gap-4">
                <div className="text-xs sm:text-sm font-medium">Reel type:</div>
                <RadioGroup
                  value={reelType}
                  onValueChange={(v) => setReelType(v as typeof reelType)}
                  className="flex items-center gap-3"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem id="reel-quick" value="quick" />
                    <Label htmlFor="reel-quick" className="text-xs sm:text-sm">Quick Explosive Reel</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem id="reel-typical" value="typical" />
                    <Label htmlFor="reel-typical" className="text-xs sm:text-sm">Typical 1-minute Reel</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem id="reel-detailed" value="detailed" />
                    <Label htmlFor="reel-detailed" className="text-xs sm:text-sm">Detailed Reel</Label>
                  </div>
                </RadioGroup>
                <Button
                  variant="default"
                  disabled={buildingContext || buildingScript || !selectedIdea}
                  onClick={async () => {
                    if (!selectedIdea) return
                    try {
                      // reset previous outputs and show loaders
                      setExtraContext([])
                      setScript(null)
                      setBuildingContext(true)
                      setBuildingScript(true)
                      const context_items = selected.map((s) => ({ type: s.type, id: s.id }))
                      const resp = await api.post('/v0_1/shorts/context', {
                        selected_idea: selectedIdea.text,
                        reel_type: reelType,
                        context_items,
                      })
                      toast({ title: 'Built additional context from the web' })
                      setExtraContext(resp.items || [])
                      // Kick off script generation in background (no extra clicks)
                      try {
                        const scriptResp = await api.post('/v0_1/shorts/script', {
                          selected_idea: selectedIdea.text,
                          reel_type: reelType,
                          context_items,
                          extra_summaries: (resp.items || []).map((it: any) => it.summary),
                        })
                        setScript(scriptResp.script || scriptResp.script_raw)
                      } catch (e) {
                        toast({ title: 'Script generation failed', variant: 'destructive' })
                      } finally {
                        setBuildingScript(false)
                      }
                    } catch (e) {
                      toast({ title: 'Context fetch failed', variant: 'destructive' })
                    } finally {
                      setBuildingContext(false)
                    }
                  }}
                >
                  {buildingContext || buildingScript ? 'Generating...' : 'Generate Script'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {script && (
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-50/50 dark:bg-green-950/20 flex-shrink-0">
                <Video className="h-4 w-4 text-green-600 dark:text-green-400" />
              </div>
              <div className="flex-1">
                <CardTitle className="text-lg font-semibold">Final Script</CardTitle>
                <CardDescription>Generated based on your selected idea and context.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {typeof script === 'object' ? (
              <>
                {script.topic ? (
                  <div className="space-y-2">
                    <div className="text-xs uppercase text-muted-foreground">Topic</div>
                    <div className="text-lg font-semibold tracking-tight">{script.topic}</div>
                  </div>
                ) : null}

                {/* Preserve narrative order: Hook → Payoff → Angle → Pattern Interrupts → Proof Points → CTA */}
                <div className="space-y-4">
                  {script.hook ? (
                    <div className="space-y-2">
                      <Badge variant="secondary" className="text-xs">Hook</Badge>
                      <div className="whitespace-pre-wrap rounded-md border border-border/50 bg-muted/30 p-3 text-sm leading-6">
                        {script.hook}
                      </div>
                    </div>
                  ) : null}

                  {script.payoff ? (
                    <div className="space-y-2">
                      <Badge variant="secondary" className="text-xs">Payoff</Badge>
                      <div className="whitespace-pre-wrap rounded-md border border-border/50 bg-muted/30 p-3 text-sm leading-6">
                        {script.payoff}
                      </div>
                    </div>
                  ) : null}

                  {script.angle ? (
                    <div className="space-y-2">
                      <Badge variant="secondary" className="text-xs">Angle</Badge>
                      <div className="whitespace-pre-wrap rounded-md border border-border/50 bg-muted/30 p-3 text-sm leading-6">
                        {script.angle}
                      </div>
                    </div>
                  ) : null}
                </div>

                {Array.isArray(script.pattern_interrupts) && script.pattern_interrupts.length ? (
                  <div className="space-y-2">
                    <Badge variant="outline" className="text-xs">Pattern Interrupts</Badge>
                    <ul className="rounded-md border border-border/50 bg-muted/30 p-3 list-disc pl-5 space-y-2 text-sm leading-6">
                      {script.pattern_interrupts.map((pi: string, i: number) => (
                        <li key={i}>{pi}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {Array.isArray(script.proof_points) && script.proof_points.length ? (
                  <div className="space-y-2">
                    <Badge variant="outline" className="text-xs">Proof Points</Badge>
                    <ul className="rounded-md border border-border/50 bg-muted/30 p-3 list-disc pl-5 space-y-2 text-sm leading-6">
                      {script.proof_points.map((pp: string, i: number) => (
                        <li key={i}>{pp}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {script.cta ? (
                  <div className="space-y-2">
                    <Badge variant="secondary" className="text-xs">CTA</Badge>
                    <div className="whitespace-pre-wrap rounded-md border border-border/50 bg-muted/30 p-3 text-sm leading-6">
                      {script.cta}
                    </div>
                  </div>
                ) : null}
              </>
            ) : (
              <div className="text-sm text-muted-foreground whitespace-pre-wrap">{String(script)}</div>
            )}
          </CardContent>
        </Card>
      )}

      {buildingScript && (
        <Card>
          <CardHeader>
            <CardTitle>Final Script</CardTitle>
            <CardDescription>Generating...</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground">Please wait while we tailor a script to your idea.</div>
          </CardContent>
        </Card>
      )}
      {(buildingContext || extraContext.length > 0) && (
        <Card>
          <CardHeader>
            <CardTitle>Additional Relevant Context</CardTitle>
            <CardDescription>Auto-fetched based on your idea and context.</CardDescription>
          </CardHeader>
          <CardContent>
            {buildingContext ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="rounded border border-border/50 p-3">
                    <Skeleton className="h-4 w-2/3 mb-2" />
                    <Skeleton className="h-3 w-full mb-1" />
                    <Skeleton className="h-3 w-5/6" />
                  </div>
                ))}
              </div>
            ) : (
              <Accordion type="single" collapsible className="w-full">
                {extraContext.map((c, i) => (
                  <AccordionItem value={`ctx-${i}`} key={i} className="border-b border-border/50">
                    <AccordionTrigger className="text-left">
                      <div className="text-sm font-medium truncate pr-4">{c.query}</div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="text-sm text-muted-foreground mb-2">
                        {c.summary}
                      </div>
                      {c.sources?.length ? (
                        <div className="flex flex-wrap gap-2">
                          {c.sources.map((s, j) => (
                            <a key={j} href={s.url} target="_blank" rel="noreferrer" className="text-xs underline underline-offset-2">
                              {s.title}
                            </a>
                          ))}
                        </div>
                      ) : null}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
